// client/constants/glass.ts
// ============================================================================
// Park Avenue — Glassmorphism & Dopamine Physics
// ============================================================================
//
// The design brief is written in CSS. Most of it is directly expressible in
// React Native and needs no special-casing:
//
//   ✅ Four-edge lighting  — borderTopColor/LeftColor/RightColor/BottomColor
//                            are all individually settable in RN.
//   ✅ The 3D press        — transform: [{perspective}, {scale}, {rotateX}]
//                            is valid RN and animates on the UI thread.
//   ✅ The spring overshoot — withSpring at low damping IS
//                            cubic-bezier(0.34, 1.56, 0.64, 1). A bezier is
//                            an approximation of a spring, not the reverse.
//
// Exactly two things are genuinely web-only, and only those are behind a
// Platform check:
//
//   ⚠️ backdrop-filter    — no RN equivalent. Native uses expo-blur's
//                            <BlurView>, which is a real GPU blur and in fact
//                            looks better; it just cannot be expressed as a
//                            style property.
//   ⚠️ inset + multi-layer shadows — RN accepts ONE shadow per view and has no
//                            inset. The brief stacks three layers, and the
//                            inset white highlight is what sells the "lit
//                            glass edge". Web gets the real string; native
//                            gets the single-layer reduction from
//                            Shadows.cardLight/floatLight plus the border
//                            lighting, which carries most of the effect.
//
// How the web values reach the DOM: react-native-web compiles the style object
// to atomic CSS and passes through properties it does not recognise as RN
// styles. So `backdropFilter` and `boxShadow` land as real CSS declarations.
// They are typed `any` because RN's ViewStyle rightly does not declare them.

import { Platform } from "react-native";
import type { ViewStyle } from "react-native";
import { BorderRadius, Shadows, type Theme } from "./theme";

const isWeb = Platform.OS === "web";

/** Style keys RN's types reject but react-native-web forwards to CSS. */
type WebStyle = Record<string, string | number>;

function web(style: WebStyle): ViewStyle {
  return (isWeb ? style : {}) as ViewStyle;
}

// ============================================================================
// Glass surfaces
// ============================================================================

export const Glass = {
  /**
   * The frosted card. Not a plain blur — the edges are lit from a single
   * overhead source, so top/left catch the light at high alpha and
   * right/bottom fall away. That asymmetry is what reads as physical glass
   * rather than as a translucent rectangle.
   *
   * Pass `float` for hero/action cards that should sit further off the page.
   */
  surface(theme: Theme, isDark: boolean, float = false): ViewStyle {
    return {
      borderWidth: 1,
      borderTopColor: theme.glassEdgeTop,
      borderLeftColor: theme.glassEdgeLeft,
      borderRightColor: theme.glassEdgeRight,
      borderBottomColor: theme.glassEdgeBottom,
      ...web({
        // 135° gradient, saturation lifted so color behind the glass stays
        // vivid instead of washing grey.
        backgroundImage: `radial-gradient(circle at 88% -12%, rgba(255,255,255,${isDark ? "0.08" : "0.92"}) 0%, transparent 34%), linear-gradient(135deg, ${theme.glassFill1} 0%, ${theme.glassFill2} 100%)`,
        backdropFilter: "blur(40px) saturate(120%)",
        WebkitBackdropFilter: "blur(40px) saturate(120%)",
        boxShadow: isDark
          ? float
              ? "0 30px 60px -12px rgba(0,0,0,0.55), 0 18px 36px -18px rgba(0,0,0,0.45), 0 2px 0 rgba(0,0,0,0.32), inset 0 2px 12px rgba(255,255,255,0.06), inset 0 -2px 0 rgba(0,0,0,0.25)"
            : "0 20px 40px -12px rgba(0,0,0,0.45), 0 2px 0 rgba(0,0,0,0.28), inset 0 1px 8px rgba(255,255,255,0.05), inset 0 -2px 0 rgba(0,0,0,0.22)"
          : float
            ? "0 30px 60px -12px rgba(18,18,16,0.14), 0 18px 36px -18px rgba(18,18,16,0.11), 0 2px 0 rgba(28,27,25,0.10), inset 0 2px 12px rgba(255,255,255,0.72), inset 0 -2px 0 rgba(28,27,25,0.08)"
            : "0 20px 40px -12px rgba(18,18,16,0.12), 0 2px 0 rgba(28,27,25,0.09), inset 0 1px 8px rgba(255,255,255,0.62), inset 0 -2px 0 rgba(28,27,25,0.07)",
      }),
    };
  },

  /**
   * Dark glass — sticky headers and nav chrome. Inverted against the sand
   * canvas so the chrome reads as a separate, heavier plane.
   */
  darkSurface(theme: Theme): ViewStyle {
    return {
      backgroundColor: theme.glassDarkFill,
      borderBottomWidth: 1,
      borderBottomColor: theme.glassDarkEdge,
      ...web({
        backdropFilter: "blur(30px) saturate(150%)",
        WebkitBackdropFilter: "blur(30px) saturate(150%)",
        boxShadow: "0 10px 30px -10px rgba(0,0,0,0.6)",
      }),
    };
  },

  /**
   * The pressed state's inner flash. On web this is an inset highlight; on
   * native it is approximated by an absolutely-positioned white overlay
   * inside the card (see GlassSurface).
   */
  pressedShadow(): ViewStyle {
    return web({
      boxShadow:
        "0 5px 15px -10px rgba(18,18,16,0.2), inset 0 6px 24px rgba(255,255,255,0.9), inset 0 0 0 1px rgba(255,255,255,1)",
    });
  },

  /** blur() intensity for expo-blur on native, matched to the web values. */
  blurIntensity: { card: 40, header: 30 },
};

/**
 * Card surface as a style ARRAY, for screens that hold a `theme` rather than
 * the floor palette. The counterpart to floorGlass() — same formula, different
 * call-site ergonomics — so the two cannot drift.
 *
 * ⚠️ CARDS ONLY. Several screens use `theme.surfaceCard` as the fill for
 * progress-bar TRACKS (confTrack, exposureBarTrack, dependenceBar). Frosting a
 * 6px bar is visually wrong and pays backdrop-filter cost for nothing — those
 * keep the flat fill.
 */
export function cardSurface(theme: Theme, isDark: boolean, float = false): ViewStyle[] {
  return [
    {
      backgroundColor: isDark ? "rgba(19,26,42,0.62)" : "rgba(255,255,255,0.62)",
      borderRadius: BorderRadius.outer,
    },
    Glass.surface(theme, isDark, float),
    isDark
      ? float ? Shadows.floatDark : Shadows.cardDark
      : float ? Shadows.floatLight : Shadows.cardLight,
  ];
}

// ============================================================================
// Dopamine physics
// ============================================================================
//
// The brief's two curves, as spring configs.
//
// PRESS is deliberately NOT a spring: the brief specifies
// `cubic-bezier(0.4, 0, 0.2, 1)` over 0.1s — a fast, damped, non-overshooting
// move. A spring here would wobble on the way DOWN, which feels loose. Use
// withTiming for the press.
//
// RELEASE is a critically damped spring. It returns quickly without the
// decorative wobble that makes a dense intelligence surface feel playful.

export const Physics = {
  /** Depress: 0.1s, no overshoot. */
  pressDuration: 100,
  /** Release: critically damped return, with no decorative bounce. */
  releaseSpring: { stiffness: 420, damping: 40, mass: 1 },

  /** Geometry of the depressed state. */
  press: {
    scale: 0.98,
    rotateX: "0.2deg",
    translateZ: -6,
    perspective: 1000,
  },
};

/**
 * The press glow — a brand-tinted bloom that suffuses a surface while it is
 * held. FEEDBACK ONLY: it appears identically on every interactive surface,
 * whether that card's record is held or withheld, so it never encodes
 * confidence or severity. Claim-coded colour belongs to
 * NeuralCard.variantGlow — not here.
 *
 * ⚠️ Web performance: animate the glow layer's OPACITY only, never its
 * boxShadow or shadowRadius — an animated shadow on many simultaneous cards
 * was measured once at +240ms TBT. The layer is a pre-rendered fill (see
 * GlassSurface), exactly like the existing press flash.
 */
export const Glow = {
  /** Peak opacity of the glow layer while the surface is held. Kept under the
   *  white press flash so the two stack instead of fighting. */
  pressOpacity: 0.12,
  /** Brand tint, from the theme's semantic tokens — never a literal. */
  color(theme: Theme): string {
    return theme.primary;
  },
};

/**
 * The entrance. Elements rise from a blur, mimicking an eye focusing —
 * a fade alone reads flat by comparison.
 *
 * Note `filter: blur()` is web-only; on native the scale + translate carry it.
 * Stagger with `index * 60`ms; beyond ~8 items the stagger outlasts patience,
 * so callers should cap the index they pass.
 */
export const Entrance = {
  duration: 240,
  staggerMs: 35,
  /** Cap the stagger so long lists don't animate for ten seconds. */
  maxStaggerIndex: 4,
  from: { opacity: 0, translateY: 12, scale: 0.99, blur: 2 },
};

/**
 * Ambient background motion. The loops are prime-numbered seconds (14/18/22)
 * so they never phase-align — a shared period would make the background
 * visibly "pulse" in unison, which reads as a loading state rather than as
 * atmosphere.
 */
export const Ambient = {
  periodsMs: [14000, 18000, 22000] as const,
  opacityRange: [0.3, 0.5] as const,
  scaleRange: [1, 1.1] as const,
  driftPx: 20,
};

/**
 * Z-index hierarchy. Kept in one place because the layering bug — chrome
 * disappearing behind a card's shadow — is invisible until it isn't.
 */
export const Layer = {
  ambient: 0,
  content: 10,
  cardContent: 20, // data floats above the glass (brief: translateZ 24px)
  header: 50,
  modal: 100,
};
